'use client';

/**
 * `useBuyTrade` — SDK-owned buy mutation. Reads host bindings from
 * `<TradingHostProvider>` via `useTradingHostContext()`.
 */
import { BN } from '@coral-xyz/anchor';
import { useRef } from 'react';
import { requireTradeMarketConfig } from '../../../core/internal/market-config-catalog.js';
import { createTradeConfirm, isAmbiguousLandingError, LANDING_GRACE_MS } from '../../../tx-engine/watch/trade-confirm.js';
import { reconcileThesisReplyEligibility } from './helpers/reconcile-thesis-reply-eligibility.js';
import { reconcileSettledTradeEntities } from './helpers/reconcile-trade-entities.js';
import { trackTxException, trackTxFailure, trackTxSuccess } from './helpers/telemetry.js';
import { showCancelledToast, showFailureToast, showSubmittingToast, showSuccessToast } from './helpers/toasts.js';
import { nextTxNumber } from './helpers/trace.js';
import { useTradingHostContext } from './host-context.js';
import { useTradeMutation } from './use-trade-mutation.js';
export function useBuyTrade() {
  const deps = useTradingHostContext();
  const abortControllerRef = useRef(null);
  const mutation = useTradeMutation(deps, 'buy', {
    mutationFn: async (params, traceId, deps_0) => {
      if (params.signingDeps === undefined) {
        throw new Error('signingDeps is required for buy transactions');
      }
      const signingDeps = params.signingDeps;
      // Capture before submission: NATS/REST may publish the trade before success fires.
      const updatePosition = deps_0.optimisticPositions?.prepare?.() ?? deps_0.optimisticPositions?.upsert;
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const startedAt = Date.now();
      const txNum = nextTxNumber('buy');
      const formattedAmount = params.desiredAmountD6.isZero() ? '' : deps_0.formatBalanceD6(params.desiredAmountD6.toString());
      deps_0.txSubmission.startSubmission(traceId, 'buy');
      showSubmittingToast(deps_0, traceId, 'buy', txNum, formattedAmount, 1, 3, abortController);
      // NATS early-confirm: armed below (after the build import resolves) so a
      // matching indexer trade event can fire success before the slow Turnkey
      // signature poll. Declared here so the catch/finally can reach it.
      let confirm = null;
      // A throw before `submitTx` is a build failure (RPC reads, account
      // loading); the engine never saw the trade, so it must not be reported
      // as an unknown submission error.
      let stage = 'build';
      try {
        const {
          buildAmmSwap,
          buildBuy,
          CP_SWAP_PROGRAM_ID
        } = await import('@morfi/contract-sdk');
        const {
          PublicKey
        } = await import('@solana/web3.js');
        deps_0.txSubmission.updateStage(traceId, 'signing');
        const connection = deps_0.getConnection();
        // Graduated markets trade on the cp-swap pool (outside the markets
        // program): route the buy through swap_base_input (USDC→outcome) rather
        // than the bonding-curve `buy` instruction.
        const isAmm = params.marketPhase === 'amm';
        const minTokensOut = new BN(0);
        let resolvedMarketConfig;
        try {
          resolvedMarketConfig = isAmm ? undefined : requireTradeMarketConfig(deps_0.queryClient, params.marketAddress);
        } catch (error) {
          deps_0.logger.error('trade.market_config.unavailable', {
            traceId,
            marketAddress: params.marketAddress,
            reason: error instanceof Error ? error.message : 'unknown'
          });
          throw error;
        }
        const tradeAccounts = resolvedMarketConfig === undefined ? undefined : {
          marketConfig: new PublicKey(resolvedMarketConfig.marketConfigAddress),
          quoteMint: new PublicKey(resolvedMarketConfig.quoteMint),
          ...(resolvedMarketConfig.eventScheduleAddress === undefined ? {} : {
            eventSchedule: new PublicKey(resolvedMarketConfig.eventScheduleAddress)
          })
        };
        // The thesis pill / share card features the USER'S intended amount,
        // not the gross-with-fees amount that hits the chain. UX, not accounting.
        const buyShareCtx = {
          amountD6: params.desiredAmountD6.toString(),
          shareAmountD6: params.outcomeTokensOutD6.toString(),
          outcomeIndex: params.outcomeIndex
        };
        const fireBuySuccess = signature => {
          const buyCtx = {
            traceId,
            instruction: 'buy',
            walletType: params.activeWallet.type,
            walletAddress: params.activeWallet.address,
            marketAddress: params.marketAddress,
            outcomeIndex: params.outcomeIndex,
            latencyMs: Date.now() - startedAt,
            // Revenue attribution: notional = exact wallet debit (fee-inclusive).
            tradeNotionalD6: params.walletDebitD6.toString(),
            ...(params.platformFeeD6 !== undefined ? {
              platformFeeD6: params.platformFeeD6
            } : {})
          };
          trackTxSuccess(deps_0, buyCtx, signature ?? '');
          deps_0.txSubmission.setSignature(traceId, signature ?? '');
          deps_0.txSubmission.updateStage(traceId, 'confirmed');
          params.onConfirmed?.();
          showSuccessToast(deps_0, traceId, 'buy', txNum, formattedAmount, signature ?? '', params.marketAddress, params.activeWallet.address, buyShareCtx, undefined, undefined,
          // Graduated/AMM (winner) buys don't offer the thesis prompt (MOR-729).
          !isAmm);
          // Optimistically increment the held balance so the trade form's
          // holding value ticks up instantly (price is already realtime).
          deps_0.optimisticBalance?.patch({
            walletAddress: params.activeWallet.address,
            outcomeMint: params.outcomeMint,
            rawDeltaD6: BigInt(params.outcomeTokensOutD6.toString())
          });
          // Optimistically open / grow the position row across every cached
          // positions list (portfolio + market + winner) so a freshly-opened
          // position — including a graduated-winner buy with no per-user AMM
          // NATS subject — appears instantly, no polling.
          updatePosition?.({
            walletAddress: params.activeWallet.address,
            market: params.marketAddress,
            outcomeIndex: params.outcomeIndex,
            outcomeMint: params.outcomeMint,
            tokenDeltaD6: BigInt(params.outcomeTokensOutD6.toString()),
            quoteD6: BigInt(params.amountInD6.toString()),
            grossQuoteD6: BigInt(params.feeExcludedQuoteInputD6.toString()),
            // Buys never pay the conviction penalty.
            penaltyD6: BigInt(0),
            walletDebitD6: BigInt(params.walletDebitD6.toString()),
            isAmm
          });
        };
        // Arm BEFORE submit: the wire SUB warms up while the (slow) Turnkey path
        // submits, so the indexer's trade event fires success the moment the
        // trade lands. Turnkey keeps running to reconcile the authoritative sig.
        confirm = createTradeConfirm({
          wallet: params.activeWallet.address,
          market: params.marketAddress,
          outcomeIdx: params.outcomeIndex,
          side: 'buy',
          venue: isAmm ? 'amm' : 'bc',
          submitStartMs: startedAt,
          debug: (msg, ctx) => deps_0.logger.info(`[tx] ⚡ ${msg}`, {
            traceId,
            ...ctx
          })
        }, {
          fireSuccess: fireBuySuccess,
          reconcileSignature: signature_0 => {
            deps_0.txSubmission.setSignature(traceId, signature_0);
            if (!isAmm) deps_0.thesis?.reconcileDialogSignature(traceId, signature_0);
            showSuccessToast(deps_0, traceId, 'buy', txNum, formattedAmount, signature_0, params.marketAddress, params.activeWallet.address, buyShareCtx, undefined, undefined, !isAmm);
          }
        });
        const sdkResult = isAmm ? await buildAmmSwap(connection, {
          market: new PublicKey(params.marketAddress),
          outcomeIndex: params.outcomeIndex,
          user: new PublicKey(params.activeWallet.address),
          direction: 'buy',
          amountIn: params.amountInD6,
          minAmountOut: minTokensOut,
          collateralMint: new PublicKey(deps_0.getUsdcMint()),
          payer: new PublicKey(params.activeWallet.address)
        }) : await buildBuy(connection, {
          market: new PublicKey(params.marketAddress),
          outcomeIndex: params.outcomeIndex,
          user: new PublicKey(params.activeWallet.address),
          amountIn: params.amountInD6,
          minTokensOut,
          payer: new PublicKey(params.activeWallet.address),
          ...(tradeAccounts === undefined ? {} : {
            tradeAccounts
          })
        });
        stage = 'submit';
        const result = await deps_0.submitTx({
          instructions: sdkResult.instructions,
          signers: sdkResult.signerKeys
        }, {
          instructionName: isAmm ? 'swapBaseInput' : 'buy',
          traceId,
          signingDeps,
          activeWallet: params.activeWallet,
          turnkeyDeps: deps_0.turnkeyDeps,
          signal: abortController.signal,
          slippageBps: params.slippageBps,
          suggestedSlippageBps: params.suggestedSlippageBps,
          marketPhase: params.marketPhase,
          ...(isAmm ? {
            programIdHint: CP_SWAP_PROGRAM_ID.toBase58()
          } : {})
        });
        if (result.ok) {
          // Turnkey confirmed. If the NATS match already fired success this
          // only reconciles the authoritative signature; otherwise it fires.
          confirm.resolveFromTurnkey(result.signature);
          return result;
        }
        if (abortController.signal.aborted) {
          // A NATS match means the trade already landed — a late abort can't
          // un-land it, so keep the success we showed.
          if (!confirm.wasFired()) {
            showCancelledToast(deps_0, traceId);
            deps_0.txSubmission.updateStage(traceId, 'cancelled');
          }
          return result;
        }
        // Submit failed. A definitive on-chain revert (TX_CONFIRM_FAILED) is
        // AUTHORITATIVE for THIS trade — it landed and FAILED. Never let a NATS
        // match keep it as success: a match on a reverted trade can only be a
        // stale/replayed event, never this trade's own (a revert emits none).
        // For ambiguous post-send errors (e.g. a `Failed to fetch` drop AFTER
        // Turnkey submitted) the tx may still have landed — wait briefly for the
        // indexer's trade event, which is ground truth.
        const isDefinitiveRevert = result.error.code === 'TX_CONFIRM_FAILED';
        let landed = !isDefinitiveRevert && confirm.wasFired();
        if (!landed && !isDefinitiveRevert && isAmbiguousLandingError(result.error.code)) {
          landed = await confirm.awaitLanding(LANDING_GRACE_MS);
        }
        if (landed) {
          // KEEP_SUCCESS — the trade demonstrably landed (the success UX was
          // already shown by the NATS match); ignore the client-leg error.
          return {
            ok: true,
            signature: confirm.firedSignature() ?? '',
            traceId
          };
        }
        // Genuine failure. Release the watch FIRST so a late NATS event cannot
        // fire a success on top of the failure UX (closes the await-then-release gap).
        confirm.release();
        trackTxFailure(deps_0, {
          traceId,
          instruction: 'buy',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: params.outcomeIndex,
          latencyMs: Date.now() - startedAt
        }, result.error);
        deps_0.txSubmission.setError(traceId, {
          code: result.error.code,
          userMessageKey: result.error.userMessageKey
        });
        showFailureToast(deps_0, traceId, result.error, slippageBps => {
          retryWithSlippage(params, slippageBps);
        });
        return result;
      } catch (err) {
        // If the indexer already confirmed the trade landed, the thrown error
        // is a client-leg artifact (the on-chain trade succeeded) — keep success.
        if (confirm?.wasFired() === true) {
          deps_0.logger.warn('Buy leg threw after NATS-confirmed landing; keeping success', {
            traceId,
            errorClass: err instanceof Error ? 'Error' : typeof err
          });
          return {
            ok: true,
            signature: confirm.firedSignature() ?? '',
            traceId
          };
        }
        if (abortController.signal.aborted) {
          showCancelledToast(deps_0, traceId);
          deps_0.txSubmission.updateStage(traceId, 'cancelled');
          return {
            ok: false,
            error: {
              code: 'TX_CANCELLED',
              stage: 'preflight',
              userMessageKey: 'tx.errors.cancelled',
              traceId,
              recoveryAction: {
                type: 'not_retryable'
              }
            },
            traceId
          };
        }
        const code = stage === 'build' ? 'TX_BUILD_FAILED' : 'TX_UNKNOWN';
        const userMessageKey = stage === 'build' ? 'tx.errors.buildFailed' : 'tx.errors.unknown';
        deps_0.txSubmission.setError(traceId, {
          code,
          userMessageKey
        });
        showFailureToast(deps_0, traceId, {
          code,
          stage: 'preflight',
          userMessageKey,
          traceId,
          recoveryAction: {
            type: 'prompt_retry'
          }
        });
        trackTxException(deps_0, {
          traceId,
          instruction: 'buy',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: params.outcomeIndex,
          latencyMs: Date.now() - startedAt
        }, err, code);
        throw err;
      } finally {
        // Always release the NATS watch (idempotent). Any late event resolves
        // to null after release, so no success can fire post-settle.
        confirm?.release();
      }
    },
    onSettled: (data, _error, params_0) => {
      // Trades update via realtime CDC patches (trades table INSERT).
      // DO NOT also invalidate a trades query here — it races with CDC and
      // causes the chart to reset. The host-supplied callback invalidates
      // the legacy wallet/onchain/balance keys it owns.
      const signature_1 = data?.ok === true && data.signature.length > 0 ? data.signature : undefined;
      reconcileSettledTradeEntities(deps, data);
      reconcileThesisReplyEligibility(deps, params_0.marketAddress);
      deps.onMutationSettled('buy', {
        marketAddress: params_0.marketAddress,
        walletAddress: params_0.activeWallet.address,
        ...(signature_1 === undefined ? {} : {
          signature: signature_1
        })
      });
    }
  });
  function retryWithSlippage(params_1, slippageBps_0) {
    mutation.mutate({
      ...params_1,
      slippageBps: slippageBps_0
    });
  }
  return {
    ...mutation,
    abortControllerRef
  };
}